// Polls /orders/pending continuously while the DJ is authenticated —
// regardless of whether the desktop overlay is collapsed or expanded.
//
// Why continuous (vs only when collapsed)? Because collapsing the panel
// unmounts RequestsPage and discards its state. If we only started
// polling on collapse, the badge would start at 0 and stay there for
// up to ~500ms until the first fetch returned, making it look like
// "the badge is broken when I minimize." Always polling means the
// count is already correct the instant the mini R appears.
//
// Why subscribe to the api.ts access-token instead of AuthContext?
// Because in Electron mode there are TWO nested AuthProviders — one
// in index.tsx wrapping DesktopShell, and another inside App.tsx
// wrapping the routes. LoginPage updates the INNER one, but
// DesktopShell's hook reads the OUTER one. The outer never sees the
// login → hook never starts polling → badge stays at 0. Listening
// to the api.ts module-level token sidesteps the whole nesting
// issue: there's only ever one token, and any login (inner or outer
// provider) flips it.
//
// localStorage cache: every successful fetch also writes the count to
// localStorage. On a fresh launch or after a WiFi blip, the badge can
// hydrate from the last known value instantly — no flash of "0".
//
// Reconnect resilience: on a fetch failure we keep the last known count
// instead of wiping to 0. Two consecutive failures flip `isOnline` to
// false (drives the dim/grey treatment on the button). The next success
// flips it back. No reload, no forced re-login.

import { useEffect, useRef, useState } from "react";

import api, { getAccessToken, onAccessTokenChange } from "../api";

interface PendingCount {
  count: number;
  isOnline: boolean;
  // Increments every time the count goes UP, never on decrease. The
  // floating button uses this as a React `key` to re-trigger the pulse
  // animation. Stable on app-load (we don't pulse for the first fetch
  // when we go from 0 → N because the DJ hasn't seen the button yet
  // — they'd land on it already showing the count without animation).
  pulseTrigger: number;
}

const POLL_MS = 10_000;
const FAIL_THRESHOLD = 2;
const COUNT_STORAGE_KEY = "requestapp.lastPendingCount";

function readCachedCount(): number {
  try {
    const raw = localStorage.getItem(COUNT_STORAGE_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeCachedCount(n: number) {
  try {
    localStorage.setItem(COUNT_STORAGE_KEY, String(n));
  } catch {
    /* storage disabled — non-fatal */
  }
}

export function usePendingCount(): PendingCount {
  // Hydrate initial count from the localStorage cache so the badge has a
  // sensible number the moment the FloatingButton mounts, instead of
  // flashing "no badge" for 500ms until the first fetch returns.
  const [count, setCount] = useState<number>(() => readCachedCount());
  const [isOnline, setIsOnline] = useState(true);
  const [pulseTrigger, setPulseTrigger] = useState(0);

  // Auth signal — true when api.ts has an access token. See header
  // comment for why we don't read AuthContext directly.
  const [hasToken, setHasToken] = useState<boolean>(() => !!getAccessToken());
  useEffect(() => {
    return onAccessTokenChange((token) => setHasToken(!!token));
  }, []);

  const failuresRef = useRef(0);
  // If we hydrated from cache with a non-zero count, treat the hook as
  // already seeded — otherwise the next legitimately-higher count would
  // trigger a startup pulse the user has no context for.
  const seededRef = useRef(count > 0);

  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const res = await api.get<any[]>("/orders/pending");
        if (cancelled) return;
        const next = Array.isArray(res.data) ? res.data.length : 0;

        setCount((prev) => {
          // Pulse only on real increases AFTER the first successful fetch.
          // First fetch seeds the count silently; otherwise the DJ would
          // see a dramatic pulse the moment the app launches just because
          // there were already 3 requests queued.
          if (seededRef.current && next > prev) {
            setPulseTrigger((p) => p + 1);
          }
          writeCachedCount(next);
          return next;
        });
        seededRef.current = true;
        failuresRef.current = 0;
        setIsOnline(true);
      } catch {
        failuresRef.current += 1;
        if (failuresRef.current >= FAIL_THRESHOLD) {
          setIsOnline(false);
        }
        // Intentionally do NOT reset count — keep showing the last known
        // number so a 5-second Wi-Fi blip doesn't make the badge vanish.
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hasToken]);

  return { count, isOnline, pulseTrigger };
}
